import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "../../firebase";
import { AuthContext } from "../../context/AuthContext";
import TopBar from "../../components/common/TopBar";
import { buildSupportNewTicketPath } from "../../support/supportRoutes";
import "./SuspendedAccountPage.css";

function isSuspendedStatus(accountStatus) {
  return String(accountStatus || "").trim().toLowerCase() === "suspended";
}

export default function SuspendedAccountPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { safeLogout } = useContext(AuthContext);
  const user = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (!user) {
          navigate("/", { replace: true });
          return;
        }

        const snap = await getDoc(doc(db, "users", user.uid));
        if (cancelled) return;

        if (!snap.exists() || !isSuspendedStatus(snap.data()?.accountStatus)) {
          navigate("/home", { replace: true });
          return;
        }
      } catch (err) {
        console.error("SuspendedAccountPage load error:", err);
        if (!cancelled) {
          navigate("/", { replace: true });
        }
        return;
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [navigate, user]);

  const handleContactSupport = () => {
    navigate(buildSupportNewTicketPath({ type: "issue", category: "report" }));
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await safeLogout();
      navigate("/", { replace: true });
    } catch (err) {
      console.error("SuspendedAccountPage logout error:", err);
    } finally {
      setLoggingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="suspended-page">
        <div className="suspended-page__body">
          <div className="suspended-card">
            <p>{t("common.loading", { defaultValue: "Loading..." })}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="suspended-page">
      <TopBar
        title={t("account.suspended_title", {
          defaultValue: "Account restricted",
        })}
      />

      <div className="suspended-page__body">
        <div className="suspended-card">
          <div className="suspended-card__icon" aria-hidden>
            ⛔
          </div>

          <h1 className="suspended-card__title">
            {t("account.suspended_title", {
              defaultValue: "Account restricted",
            })}
          </h1>

          <p className="suspended-card__message">
            {t("account.suspended_message", {
              defaultValue:
                "This account has been temporarily restricted. If you believe this is a mistake, you can contact support.",
            })}
          </p>

          <div className="suspended-card__actions">
            <button
              type="button"
              className="suspended-card__btn suspended-card__btn--primary"
              onClick={handleContactSupport}
            >
              {t("account.suspended_contact_support", {
                defaultValue: "Contact support",
              })}
            </button>

            <button
              type="button"
              className="suspended-card__btn suspended-card__btn--secondary"
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut
                ? t("common.loading", { defaultValue: "Loading..." })
                : t("auth.logout", { defaultValue: "Log out" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
